import { HttpClient, httpResource } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';

export const DEMO_INSTALLATION_URL = '/api/dev/demo/installation';

export type DemoInstallationId = 'global' | 'indonesia';
export type DemoRegistrationId = 'eu-waitlist' | 'eu-licensed';

interface DemoInstallationState {
  readonly installation: DemoInstallationId;
  readonly registration?: DemoRegistrationId;
  readonly available: readonly DemoInstallationId[];
  readonly urls: Readonly<Partial<Record<DemoInstallationId, string>>>;
}

interface DemoInstallationSwitch {
  readonly installation: DemoInstallationId;
  readonly signedIn: boolean;
}

export const DEMO_INSTALLATION_SWITCH_URL = '/api/dev/demo/installation/active';

export interface DemoOption<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly caption: string;
}

export const DEMO_INSTALLATION_OPTIONS: readonly DemoOption<DemoInstallationId>[] = [
  { id: 'global', label: 'Global', caption: 'NOXO Global Ltd. · ECSPR rules · EUR · its own database and personas' },
  {
    id: 'indonesia',
    label: 'Indonesia',
    caption: 'PT NOXO Indonesia · OJK sandbox · IDR · its own database and personas',
  },
];

export const DEMO_REGISTRATION_OPTIONS: readonly DemoOption<DemoRegistrationId>[] = [
  {
    id: 'eu-waitlist',
    label: 'EU waitlist',
    caption: 'Today: EU/EEA residents join the waitlist, Indonesia is referred',
  },
  { id: 'eu-licensed', label: 'EU licensed', caption: 'After the ECSP licence: EU/EEA residents register' },
];

@Injectable()
export class DemoInstallation {
  private readonly http = inject(HttpClient);
  private readonly resource = httpResource<DemoInstallationState>(() => DEMO_INSTALLATION_URL);
  private readonly state = computed(() => (this.resource.hasValue() ? this.resource.value() : undefined));

  public readonly installation = computed(() => this.state()?.installation);
  public readonly registration = computed(() => this.state()?.registration);
  public readonly urls = computed(() => this.state()?.urls);
  public readonly available = computed(() => this.state()?.available ?? []);

  public switchTo(id: DemoInstallationId): Promise<DemoInstallationSwitch> {
    return firstValueFrom(this.http.put<DemoInstallationSwitch>(DEMO_INSTALLATION_SWITCH_URL, { installation: id }));
  }

  public async useRegistration(id: DemoRegistrationId): Promise<void> {
    await firstValueFrom(this.http.put<void>(DEMO_INSTALLATION_URL, { registration: id }));
    this.resource.reload();
  }
}
